import dayjs from 'dayjs'
import type {Dayjs} from 'dayjs'
import {read, utils, write} from 'xlsx'
import type {CellObject, WorkSheet} from 'xlsx'
import {CASAS_DECIMAIS_PRECO_COMPRA, calcularValorTotal, criarEdicaoDoItem} from './conferencia-itens'
import type {EdicaoItensOrdemCompra, ItemOrdemCompraEditado} from './conferencia-itens'
import {CABECALHOS_PLANILHA_ITENS} from './content'
import type {FollowUpItemOrdemCompraResponse} from '../../types'

export type ErroImportacaoPlanilha = 'arquivo-invalido' | 'cabecalho-invalido' | 'sem-itens-correspondentes'

export type ResultadoImportacaoPlanilha =
    | {
        sucesso: true;
        edicoes: EdicaoItensOrdemCompra;
        quantidadeItensAtualizados: number;
        codigosNaoEncontrados: string[];
    }
    | {sucesso: false; erro: ErroImportacaoPlanilha}

type ColunaImportada = 'codigo' | 'precoCompra' | 'quantidade' | 'justificativa' | 'previsaoEntrega'

type IndicesColunasImportadas = Record<ColunaImportada, number>

const COLUNAS_IMPORTADAS: ColunaImportada[] = ['codigo', 'precoCompra', 'quantidade', 'justificativa', 'previsaoEntrega']

const LARGURAS_COLUNAS = [10.57, 12.64, 33.64, 8.36, 9.64, 10.14, 9.07, 10.79]

const NOME_ABA = 'Plan1'

const TIPO_ARQUIVO_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const montarFormatoMoeda = (casasDecimais: number) => {
    const numero = `#,##0.${'0'.repeat(casasDecimais)}`

    return `_-R$ * ${numero}_-;-R$ * ${numero}_-;_-R$ * "-"??_-;_-@_-`
}

const FORMATO_MOEDA = montarFormatoMoeda(2)

const FORMATO_PRECO_COMPRA = montarFormatoMoeda(CASAS_DECIMAIS_PRECO_COMPRA)

const FORMATO_DATA = 'dd/mm/yyyy'

const MILISSEGUNDOS_POR_DIA = 86_400_000

const INICIO_DATAS_EXCEL = Date.UTC(1899, 11, 30)

const PADRAO_DATA_TEXTO = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/

const obterEdicao = (item: FollowUpItemOrdemCompraResponse, edicoes: EdicaoItensOrdemCompra) =>
    edicoes[item.idPreCompraItem] ?? criarEdicaoDoItem(item)

function converterDataParaSerialExcel(data: Dayjs): number {
    const instanteLocalEmUtc = Date.UTC(data.year(), data.month(), data.date(), data.hour(), data.minute(), data.second())

    return (instanteLocalEmUtc - INICIO_DATAS_EXCEL) / MILISSEGUNDOS_POR_DIA
}

function converterSerialExcelParaData(serial: number): Dayjs | null {
    if (!Number.isFinite(serial) || serial <= 0) return null

    const instante = new Date(Math.round((INICIO_DATAS_EXCEL + serial * MILISSEGUNDOS_POR_DIA) / 1000) * 1000)

    return dayjs(new Date(
        instante.getUTCFullYear(),
        instante.getUTCMonth(),
        instante.getUTCDate(),
        instante.getUTCHours(),
        instante.getUTCMinutes(),
        instante.getUTCSeconds(),
    ))
}

const celulaTexto = (valor: string | null): CellObject | null =>
    valor === null || valor.length === 0 ? null : {t: 's', v: valor}

const celulaNumero = (valor: number | null, formato?: string): CellObject | null => {
    if (valor === null) return null

    return formato === undefined ? {t: 'n', v: valor} : {t: 'n', v: valor, z: formato}
}

function montarLinhaDoItem(
    item: FollowUpItemOrdemCompraResponse,
    edicao: ItemOrdemCompraEditado,
    numeroLinha: number,
): (CellObject | null)[] {
    const valorTotal = calcularValorTotal(edicao)

    return [
        celulaNumero(item.codigo),
        celulaTexto(item.codigoFabrica),
        celulaTexto(item.descricao),
        celulaNumero(edicao.precoCompra, FORMATO_PRECO_COMPRA),
        celulaNumero(edicao.quantidade),
        celulaTexto(edicao.justificativa),
        valorTotal === null ? null : {t: 'n', v: valorTotal, f: `D${numeroLinha}*E${numeroLinha}`, z: FORMATO_MOEDA},
        edicao.previsaoEntrega === null
            ? null
            : celulaNumero(converterDataParaSerialExcel(edicao.previsaoEntrega), FORMATO_DATA),
    ]
}

function montarAbaDosItens(itens: FollowUpItemOrdemCompraResponse[], edicoes: EdicaoItensOrdemCompra): WorkSheet {
    const cabecalho = Object.values(CABECALHOS_PLANILHA_ITENS).map(celulaTexto)
    const linhas = [
        cabecalho,
        ...itens.map((item, indice) => montarLinhaDoItem(item, obterEdicao(item, edicoes), indice + 2)),
    ]
    const aba: WorkSheet = {}

    linhas.forEach((linha, r) => {
        linha.forEach((celula, c) => {
            if (celula !== null) aba[utils.encode_cell({r, c})] = celula
        })
    })

    aba['!ref'] = utils.encode_range({s: {r: 0, c: 0}, e: {r: linhas.length - 1, c: cabecalho.length - 1}})
    aba['!cols'] = LARGURAS_COLUNAS.map(wch => ({wch}))

    return aba
}

export function montarConteudoPlanilhaItens(
    itens: FollowUpItemOrdemCompraResponse[],
    edicoes: EdicaoItensOrdemCompra,
): ArrayBuffer {
    const pasta = utils.book_new()
    utils.book_append_sheet(pasta, montarAbaDosItens(itens, edicoes), NOME_ABA)

    return write(pasta, {bookType: 'xlsx', type: 'array'}) as ArrayBuffer
}

export function gerarArquivoPlanilhaItens(
    itens: FollowUpItemOrdemCompraResponse[],
    edicoes: EdicaoItensOrdemCompra,
    numeroOrdem: number,
): File {
    return new File([montarConteudoPlanilhaItens(itens, edicoes)], `${numeroOrdem}.xlsx`, {type: TIPO_ARQUIVO_XLSX})
}

const normalizarTexto = (valor: unknown): string =>
    String(valor ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()

function localizarColunas(cabecalho: unknown[]): IndicesColunasImportadas | null {
    const cabecalhoNormalizado = cabecalho.map(normalizarTexto)
    const indices = {} as IndicesColunasImportadas

    for (const coluna of COLUNAS_IMPORTADAS) {
        const indice = cabecalhoNormalizado.indexOf(normalizarTexto(CABECALHOS_PLANILHA_ITENS[coluna]))
        if (indice < 0) return null

        indices[coluna] = indice
    }

    return indices
}

function lerCodigo(valor: unknown): string | null {
    if (typeof valor === 'number') return Number.isFinite(valor) ? String(valor) : null
    if (typeof valor !== 'string') return null

    const codigo = valor.trim()
    if (codigo.length === 0) return null

    return /^\d+$/.test(codigo) ? String(Number(codigo)) : codigo
}

function lerNumero(valor: unknown): number | null {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null
    if (typeof valor !== 'string') return null

    const texto = valor.replace(/[R$\s]/g, '')
    if (texto.length === 0) return null

    const numero = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto)

    return Number.isFinite(numero) ? numero : null
}

function lerNumeroNaoNegativo(valor: unknown, casasDecimais: number): number | null {
    const numero = lerNumero(valor)
    if (numero === null || numero < 0) return null

    const fator = 10 ** casasDecimais

    return Math.round(numero * fator) / fator
}

function lerTexto(valor: unknown): string | null {
    if (valor === null || valor === undefined) return null

    const texto = String(valor).trim()

    return texto.length === 0 ? null : texto
}

function lerDataDeTexto(texto: string): Dayjs | null {
    const partes = PADRAO_DATA_TEXTO.exec(texto.trim())
    if (partes === null) return null

    const [, dia, mes, ano] = partes.map(Number)
    const data = new Date(ano, mes - 1, dia)
    const isDataExistente = data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia

    return isDataExistente ? dayjs(data) : null
}

function lerData(valor: unknown): Dayjs | null {
    if (typeof valor === 'number') return converterSerialExcelParaData(valor)
    if (typeof valor === 'string') return lerDataDeTexto(valor)

    return null
}

function lerAlteracaoDaLinha(linha: unknown[], indices: IndicesColunasImportadas): Partial<ItemOrdemCompraEditado> {
    const alteracao: Partial<ItemOrdemCompraEditado> = {}
    const precoCompra = lerNumeroNaoNegativo(linha[indices.precoCompra], CASAS_DECIMAIS_PRECO_COMPRA)
    const quantidade = lerNumeroNaoNegativo(linha[indices.quantidade], 0)
    const justificativa = lerTexto(linha[indices.justificativa])
    const previsaoEntrega = lerData(linha[indices.previsaoEntrega])

    if (precoCompra !== null) alteracao.precoCompra = precoCompra
    if (quantidade !== null) alteracao.quantidade = quantidade
    if (justificativa !== null) alteracao.justificativa = justificativa
    if (previsaoEntrega !== null) alteracao.previsaoEntrega = previsaoEntrega

    return alteracao
}

function lerLinhasDaPrimeiraAba(conteudo: ArrayBuffer): unknown[][] | null {
    try {
        const pasta = read(new Uint8Array(conteudo), {type: 'array'})
        const nomeAba = pasta.SheetNames[0]
        if (nomeAba === undefined) return null

        return utils.sheet_to_json<unknown[]>(pasta.Sheets[nomeAba], {header: 1, blankrows: false, defval: null, raw: true})
    } catch {
        return null
    }
}

export function lerPlanilhaItens(
    conteudo: ArrayBuffer,
    itens: FollowUpItemOrdemCompraResponse[],
    edicoes: EdicaoItensOrdemCompra,
): ResultadoImportacaoPlanilha {
    const linhas = lerLinhasDaPrimeiraAba(conteudo)
    if (linhas === null) return {sucesso: false, erro: 'arquivo-invalido'}

    const [cabecalho = [], ...linhasDeItens] = linhas
    const indices = localizarColunas(cabecalho)
    if (indices === null) return {sucesso: false, erro: 'cabecalho-invalido'}

    const edicoesImportadas: EdicaoItensOrdemCompra = {}
    const codigosNaoEncontrados = new Set<string>()

    for (const linha of linhasDeItens) {
        const codigo = lerCodigo(linha[indices.codigo])
        if (codigo === null) continue

        const itensDoCodigo = itens.filter(item => item.codigo !== null && String(item.codigo) === codigo)
        if (itensDoCodigo.length === 0) {
            codigosNaoEncontrados.add(codigo)
            continue
        }

        const alteracao = lerAlteracaoDaLinha(linha, indices)

        for (const item of itensDoCodigo) {
            const edicaoAtual = edicoesImportadas[item.idPreCompraItem] ?? obterEdicao(item, edicoes)
            edicoesImportadas[item.idPreCompraItem] = {...edicaoAtual, ...alteracao}
        }
    }

    const quantidadeItensAtualizados = Object.keys(edicoesImportadas).length
    if (quantidadeItensAtualizados === 0) return {sucesso: false, erro: 'sem-itens-correspondentes'}

    return {
        sucesso: true,
        edicoes: edicoesImportadas,
        quantidadeItensAtualizados,
        codigosNaoEncontrados: [...codigosNaoEncontrados],
    }
}
